import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router';
import { Property, nearbyPlaces } from '../../data/mockData';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { ArrowLeft, Save, X, MapPin } from 'lucide-react';
import { useProperties } from '../../contexts/PropertyContext';
import { useCategories } from '../../contexts/CategoriesContext';
import { toast } from 'sonner';
import { uploadImage } from '../../lib/supabaseStorage';

const egyptianCities = [
  'القاهرة', 'الجيزة', 'الإسكندرية', 'الدقهلية', 'الشرقية', 'الغربية', 'المنوفية', 'القليوبية',
  'الفيوم', 'بني سويف', 'المنيا', 'أسيوط', 'سوهاج', 'قنا', 'الأقصر', 'أسوان', 'البحيرة',
  'كفر الشيخ', 'الإسماعيلية', 'السويس', 'بورسعيد', 'دمياط', 'العريش', 'طابا', 'شرم الشيخ',
  'الغردقة', 'الغردقة', 'العين السخنة', 'الفيوم', '6 أكتوبر', 'الشيخ زايد', 'مدينة نصر',
  'المعادي', 'التجمع الخامس', 'التجمع الأول', 'حلوان', 'شبرا', 'العباسية', 'الدقي', 'المهندسين',
  'الزمالك', 'مصر الجديدة', 'هيليوبوليس', 'الرحاب', 'مدينة الشروق', 'بدر', 'العاصمة الإدارية'
];

export default function EditProperty() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { properties, updateProperty, loading } = useProperties();
  const { categories, loading: categoriesLoading } = useCategories();
  
  const [property, setProperty] = useState<Property | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    price: '',
    area: '',
    bedrooms: '',
    bathrooms: '',
    garages: '',
    floor: '',
    location: '',
    type: 'sale' as 'sale' | 'rent',
    category: '', // Will be set when categories load
    description: '',
    videoUrl: '',
    adminNote: '',
    featured: false,
    active: true,
    mainImage: '',
    galleryImages: [] as string[],
    nearby: [] as string[],
    mapsUrl: ''
  });
  const [saving, setSaving] = useState(false);
  const [locationInput, setLocationInput] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const locationRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const found = properties.find(p => p.id === id);
    if (found && !isInitialized) {
      setProperty(found);
      setFormData({
        title: found.title,
        price: found.price.toString(),
        area: found.area.toString(),
        bedrooms: found.bedrooms.toString(),
        bathrooms: found.bathrooms.toString(),
        garages: found.garages.toString(),
        floor: found.floor?.toString() || '1',
        location: found.location,
        type: found.type,
        category: found.category,
        description: found.description,
        videoUrl: found.videoUrl || '',
        mapsUrl: found.mapsUrl || '',
        adminNote: found.adminNote || '',
        featured: found.featured,
        active: found.active,
        mainImage: found.mainImage,
        galleryImages: found.galleryImages || [],
        nearby: found.nearby || []
      });
      setLocationInput(found.location);
      setIsInitialized(true);
    }
  }, [id, properties, isInitialized]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (locationRef.current && !locationRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleMainImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const localUrl = URL.createObjectURL(file);
      handleInputChange('mainImage', localUrl);
      
      try {
        const imageUrl = await uploadImage(file);
        handleInputChange('mainImage', imageUrl);
        toast.success('تم رفع الصورة الرئيسية');
      } catch (error) {
        toast.error('فشل رفع الصورة على الخادم، سيتم عرضها محلياً');
      }
    }
  };

  const handleGalleryUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      const localUrls = files.map(file => URL.createObjectURL(file));
      const prevGallery = formData.galleryImages;
      
      handleInputChange('galleryImages', [...prevGallery, ...localUrls]);
      
      try {
        const urls = await Promise.all(files.map(file => uploadImage(file)));
        handleInputChange('galleryImages', [...prevGallery, ...urls]);
        toast.success(`تم رفع ${files.length} صور`);
      } catch (error) {
        toast.error('فشل رفع بعض الصور على الخادم، سيتم عرضها محلياً');
      }
    }
  };

  const removeGalleryImage = (index: number) => {
    handleInputChange('galleryImages', formData.galleryImages.filter((_, i) => i !== index));
  };

  const toggleNearby = (placeId: string) => {
    setFormData(prev => ({
      ...prev,
      nearby: prev.nearby.includes(placeId)
        ? prev.nearby.filter(id => id !== placeId)
        : [...prev.nearby, placeId]
    }));
  };

  const handleLocationSelect = (city: string) => {
    handleInputChange('location', city);
    setLocationInput(city);
    setShowSuggestions(false);
  };

  const filteredCities = egyptianCities.filter(city => 
    city.toLowerCase().includes(locationInput.toLowerCase())
  );

  const handleSave = async () => {
    if (!property) return;
    
    // Validate price is reasonable (max 100 billion)
    const priceValue = parseFloat(formData.price) || 0;
    if (priceValue > 100000000000) {
      toast.error('السعر كبير جداً. أقصى قيمة مسموح بها هي 100 مليار جنيه');
      return;
    }
    if (priceValue <= 0) {
      toast.error('يرجى إدخال سعر صحيح');
      return;
    }
    
    setSaving(true);
    try {
      // Find the category ID for the selected category name
      const selectedCategory = categories.find(cat => cat.name === formData.category);
      
      const updatedProperty: Property = {
        ...property,
        title: formData.title,
        price: parseInt(formData.price),
        area: parseInt(formData.area),
        bedrooms: parseInt(formData.bedrooms),
        bathrooms: parseInt(formData.bathrooms),
        garages: parseInt(formData.garages),
        floor: parseInt(formData.floor) || 1,
        location: formData.location,
        type: formData.type,
        category: formData.category,
        categoryId: selectedCategory?.id || property.categoryId, // Use new category ID if found, fallback to existing
        description: formData.description,
        videoUrl: formData.videoUrl,
        mapsUrl: formData.mapsUrl,
        adminNote: formData.adminNote,
        featured: formData.featured,
        active: formData.active,
        nearby: formData.nearby,
        mainImage: formData.mainImage,
        galleryImages: formData.galleryImages
      };
      
      await updateProperty(id!, updatedProperty);
      toast.success('تم تحديث العقار بنجاح');
      navigate('/admin/properties');
    } catch (error) {
      toast.error('فشل تحديث العقار');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center p-8">جاري التحميل...</div>;
  }

  if (!property) {
    return <div className="text-center p-8">العقار غير موجود</div>;
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center gap-4 mb-6">
        <Link to="/admin/properties">
          <Button variant="outline" size="icon">
            <ArrowLeft className="w-4 h-4" />
          </Button>
        </Link>
        <h1 className="text-2xl font-bold">تعديل العقار</h1>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium mb-2">العنوان</label>
            <Input 
              placeholder="عنوان العقار" 
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">السعر</label>
            <Input 
              type="number" 
              placeholder="1000000" 
              value={formData.price}
              onChange={(e) => handleInputChange('price', e.target.value)}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">المساحة (م²)</label>
            <Input 
              type="number" 
              placeholder="200" 
              value={formData.area}
              onChange={(e) => handleInputChange('area', e.target.value)}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">غرف النوم</label>
            <Input 
              type="number" 
              placeholder="3" 
              value={formData.bedrooms}
              onChange={(e) => handleInputChange('bedrooms', e.target.value)}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">الحمامات</label>
            <Input 
              type="number" 
              placeholder="2" 
              value={formData.bathrooms}
              onChange={(e) => handleInputChange('bathrooms', e.target.value)}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">الجراجات</label>
            <Input 
              type="number" 
              placeholder="1" 
              value={formData.garages}
              onChange={(e) => handleInputChange('garages', e.target.value)}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">الطابق</label>
            <Input 
              type="number" 
              placeholder="1" 
              value={formData.floor}
              onChange={(e) => handleInputChange('floor', e.target.value)}
              required
            />
          </div>

          {/* Location Field with Preview */}
          <div ref={locationRef} className="relative md:col-span-2">
            <label className="block text-sm font-medium mb-2">الموقع</label>
            <div className="relative">
              <Input 
                placeholder="ابحث عن المدينة..." 
                value={locationInput}
                onChange={(e) => {
                  setLocationInput(e.target.value);
                  handleInputChange('location', e.target.value);
                  setShowSuggestions(true);
                }}
                onFocus={() => setShowSuggestions(true)}
                required
                className="pr-10"
              />
              <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            </div>
            
            {/* Location Suggestions Dropdown */}
            {showSuggestions && filteredCities.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-auto">
                {filteredCities.map((city, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleLocationSelect(city)}
                    className={`w-full text-right px-4 py-2 hover:bg-gray-100 transition-colors flex items-center gap-2 ${
                      formData.location === city ? 'bg-[#D4AF37]/10 text-[#D4AF37]' : ''
                    }`}
                  >
                    <MapPin className="w-4 h-4" />
                    {city}
                  </button>
                ))}
              </div>
            )}
            
            {/* Location Preview */}
            {formData.location && (
              <div className="mt-3 p-4 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg">
                <div className="flex items-center gap-2 text-[#8B7109]">
                  <MapPin className="w-5 h-5" />
                  <span className="font-medium">الموقع المحدد:</span>
                </div>
                <p className="mt-2 text-gray-800 font-bold text-lg">{formData.location}</p>
                <p className="mt-1 text-sm text-gray-500">
                  سيتم عرض هذا الموقع في صفحة العقار للزوار
                </p>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">التصنيف</label>
            <Select value={formData.category} onValueChange={(value) => handleInputChange('category', value)}>
              <SelectTrigger>
                <SelectValue placeholder="اختر التصنيف" />
              </SelectTrigger>
              <SelectContent>
                {categoriesLoading ? (
                  <div className="px-4 py-2 text-sm text-gray-500">جاري التحميل...</div>
                ) : categories.length > 0 ? (
                  categories.map((category) => (
                    <SelectItem key={category.id} value={category.name}>
                      {category.name}
                    </SelectItem>
                  ))
                ) : (
                  <div className="px-4 py-2 text-sm text-gray-500">لا توجد تصنيفات متاحة</div>
                )}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">النوع</label>
            <Select value={formData.type} onValueChange={(value: 'sale' | 'rent') => handleInputChange('type', value)}>
              <SelectTrigger>
                <SelectValue placeholder="اختر النوع" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="sale">للبيع</SelectItem>
                <SelectItem value="rent">للإيجار</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">الوصف</label>
          <textarea
            className="w-full min-h-[120px] px-3 py-2 border rounded-md"
            placeholder="وصف العقار..."
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">الصورة الرئيسية</label>
          <div className="flex items-center gap-4">
            {formData.mainImage && (
              <img 
                src={formData.mainImage} 
                alt="Main" 
                className="w-32 h-32 object-cover rounded-lg"
              />
            )}
            <Input 
              type="file" 
              accept="image/*"
              onChange={handleMainImageUpload}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">صور إضافية</label>
          <div className="grid grid-cols-4 gap-4 mb-4">
            {formData.galleryImages.map((img, idx) => (
              <div key={idx} className="relative">
                <img 
                  src={img} 
                  alt={`Gallery ${idx}`} 
                  className="w-full h-24 object-cover rounded-lg"
                />
                <button
                  type="button"
                  onClick={() => removeGalleryImage(idx)}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          <Input 
            type="file" 
            accept="image/*"
            multiple
            onChange={handleGalleryUpload}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">الأماكن القريبة</label>
          <div className="grid grid-cols-3 gap-4">
            {nearbyPlaces.map((place) => (
              <label key={place.id} className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={formData.nearby.includes(place.id)}
                  onCheckedChange={() => toggleNearby(place.id)}
                />
                <span className="text-sm">{place.name}</span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">رابط الفيديو</label>
          <Input 
            placeholder="https://youtube.com/..." 
            value={formData.videoUrl}
            onChange={(e) => handleInputChange('videoUrl', e.target.value)}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">رابط خرائط Google (اختياري)</label>
          <Input 
            placeholder="https://maps.google.com/..." 
            value={formData.mapsUrl}
            onChange={(e) => handleInputChange('mapsUrl', e.target.value)}
          />
          <p className="text-xs text-gray-500 mt-1">يمكنك إضافة رابط موقع العقار على Google Maps</p>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">ملاحظات المسؤول</label>
          <textarea
            className="w-full min-h-[80px] px-3 py-2 border rounded-md"
            placeholder="ملاحظات داخلية..."
            value={formData.adminNote}
            onChange={(e) => handleInputChange('adminNote', e.target.value)}
          />
        </div>

        <div className="flex items-center gap-2">
          <Checkbox
            checked={formData.featured}
            onCheckedChange={(checked) => handleInputChange('featured', checked)}
          />
          <label className="text-sm font-medium">عقار مميز</label>
        </div>

        <div className="flex items-center gap-2">
          <Checkbox
            checked={formData.active}
            onCheckedChange={(checked) => handleInputChange('active', checked)}
          />
          <label className="text-sm font-medium">Active (Visible on website)</label>
        </div>

        <div className="flex justify-end gap-4">
          <Link to="/admin/properties">
            <Button variant="outline" type="button">إلغاء</Button>
          </Link>
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="bg-[#D4AF37] hover:bg-[#B8960C] text-white"
          >
            {saving ? 'جاري الحفظ...' : 'حفظ التعديلات'}
          </Button>
        </div>
      </div>
    </div>
  );
}
